import React, { useState, useEffect } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DialogContent } from '@mui/material';
import DialogTitle from '@mui/material/DialogTitle';
import RiskOverviewSection from './risk-overview-section';
import SummaryInfo from './summary-info';
import WarningLostDataDialog from '@components-clean/legacy/unsaved-changes-dialog/warning-lost-data-dialog';
import { useTranslation, useBoolean, useAuthorization } from 'hooks';
import {
    GET_CM_REVIEW_REPORT_DATA,
    GET_CM_OBSERVATIONS_AND_ACTIONS,
    SAVE_DATASET_REVIEW,
} from 'queries/central-monitoring';
import { StudyFields, ReportFields } from 'queries/generated/fragments.generated';
import { GENERATE_PDF_REPORT, GET_PDF_REPORT, GET_REPORTS } from 'queries/reports';
import { GET_STUDY } from 'queries/studies';
import './preview-report.scss';
import {
    calculateFieldsForStudySummary,
    calculateRiskOverviewColumns,
    calculateRiskOverviewsAndStudySites,
} from 'utils/cm-review-report';
import Constants from 'utils/constants';
import { downloadBase64File } from 'utils/file-service';
import ObservationsSection from 'views/components/central-monitoring/reports/observations-section';
import SiteReport from 'views/components/central-monitoring/reports/site-report';
import StudyReview from 'views/components/central-monitoring/reports/study-review';
import Button from 'views/components/core/buttons/button';
import { CustomDialog } from 'views/components/core/dialog';
import Loading from 'views/components/core/loading';
import Settings from '@settings/settings';
import { ProcessedStudyDataResponse, ScoringStudyDataResponse } from '@apis/bff-api/studies';

const PreviewReport = ({
    open,
    onClose,
    stateForGenerate,
    study,
    template,
    reports,
}: {
    open: boolean;
    onClose: Function;
    stateForGenerate: any;
    study: StudyFields;
    template: string;
    reports: ReportFields[];
}) => {
    const { t } = useTranslation();
    const user = useAuthorization();

    const [getReportData, { data: reportData, loading: loadingReport }] = useLazyQuery(GET_CM_REVIEW_REPORT_DATA);
    const [getObservations, { data: observations, loading: loadingObservations }] = useLazyQuery(
        GET_CM_OBSERVATIONS_AND_ACTIONS,
    );
    const [generateReport, { loading }] = useMutation(GENERATE_PDF_REPORT);
    const [saveDataSetReview, { loading: loadingDataSetReview }] = useMutation(SAVE_DATASET_REVIEW);
    const [getReport, { data: PDFReport, loading: loadingPDFReport }] = useLazyQuery(GET_PDF_REPORT);

    // Apollo shares cache for all queries, in any page or component, that use the same underlying graphql query and result object
    //  Here we are querying the study with a specific timestamp based on the dataset date,
    //  and this updates the cache with potentially an older version of the study
    //  This then updates the navbar component with this older version of the study.
    //  Among other things this then caused the modules assigned to the study to change to an older version
    //  and hence hide the new central monitoring section in the page nav. It subsequently updated with a further rerender, but still not great.
    // Switch policy to 'no-cache'. This will be slower because it needs to fetch every time this query is called by legacy dashboards,
    // but it will stop updating the shared cache with an out-of-date study,
    // and it will also stop us incorrectly getting the cached info for the current study here
    const [loadStudy, { data: actualStudy, loading: loadingStudy }] = useLazyQuery(GET_STUDY, {
        fetchPolicy: 'no-cache',
    });

    const [state, setState] = useState({} as any);
    const [sites, setSites] = useState([]);
    const [siteReviews, setSiteReviews] = useState({} as any);
    const [templateName, setTemplateName] = useState('');
    const [studySummary, setStudySummary] = useState({} as any);
    const [riskOverviews, setRiskOverviews] = useState({} as any);
    const [riskOverviewColumns, setRiskOverviewColumns] = useState([] as any);
    const [errors, setErrors] = useState({} as any);
    const [processedData, setProcessedData] = useState<ProcessedStudyDataResponse | null>(null);
    const [scoringData, setScoringData] = useState<ScoringStudyDataResponse | null>(null);

    const changesWereMade = useBoolean(false);
    const showWarningModal = useBoolean(false);
    const loadingDataset = useBoolean(false);

    const loadDataSetInfo = () => {
        const datasetId = stateForGenerate.dataset?.id as string;

        loadingDataset.setTrue();

        Promise.all([
            fetch(
                datasetId
                    ? `${Settings.api.url()}/api/v1/studies/id/${study.id}/data/${datasetId}`
                    : `${Settings.api.url()}/api/v1/studies/id/${study.id}/data`,
                {
                    headers: {
                        APIKey: study.organisation?.apiKey ?? '',
                        Authorization: `Bearer ${user?.token}`,
                    },
                },
            )
                .then((result) => result.json())
                .then((result: ProcessedStudyDataResponse) => {
                    setProcessedData(result);
                })
                .catch((err) => {
                    if (err.message !== 'Unexpected end of JSON input')
                        console.error('Error getting processed data: ', err);
                    else console.warn('No processed data was found for this study.');
                }),
            fetch(
                datasetId
                    ? `${Settings.api.url()}/api/v1/studies/id/${study.id}/kri/${datasetId}`
                    : `${Settings.api.url()}/api/v1/studies/id/${study.id}/kri`,
                {
                    headers: {
                        APIKey: study.organisation?.apiKey ?? '',
                        Authorization: `Bearer ${user?.token}`,
                    },
                },
            )
                .then((result) => result.json())
                .then((result: ScoringStudyDataResponse) => {
                    setScoringData(result);
                })
                .catch((err) => {
                    if (err.message !== 'Unexpected end of JSON input')
                        console.error('Error getting scoring data: ', err);
                    else console.warn('No scoring data was found for this study.');
                }),
        ])
            .catch((err) => {
                console.error('Error getting processed and scoring data: ', err);
            })
            .finally(() => {
                loadingDataset.setFalse();
            });
    };

    useEffect(() => {
        if (!open) {
            setSiteReviews({});
            setStudySummary('');
        }
    }, [open]);

    useEffect(() => {
        if (stateForGenerate?.dataset?.id && study) loadDataSetInfo();
    }, [stateForGenerate?.dataset?.id]);

    useEffect(() => {
        if (PDFReport?.pdfReport) downloadBase64File(`${templateName}.pdf`, PDFReport.pdfReport);
    }, [PDFReport]);

    const generateReportFile = () => {
        generateReport({
            variables: {
                studyId: study?.id,
                template,
                dataSetId: stateForGenerate.dataset.id,
                onlyReviewed: stateForGenerate.includeOnlyReviewedSites,
            },
            refetchQueries: [
                {
                    query: GET_REPORTS,
                    variables: {
                        studyId: study?.id,
                        template,
                    },
                },
            ],
        })
            .then((result: any) => {
                setTemplateName(result.data.generatePdfReport.templateName);
                getReport({
                    variables: {
                        id: result.data.generatePdfReport.id,
                    },
                });
                onClose('generate');
            })
            .catch((error: any) => {
                console.log('Error while generating PDF report: ', error);
                onClose('error');
            });
    };

    const submit = async () => {
        if (Object.keys(siteReviews)?.length) {
            const dataForRequest: any[] = [];
            for (const item in siteReviews) {
                dataForRequest.push(siteReviews[item]);
            }
            try {
                const results = await Promise.all(
                    dataForRequest.map((data: any) =>
                        saveDataSetReview({
                            variables: {
                                ...data,
                            },
                        }),
                    ),
                );

                if (results) {
                    generateReportFile();
                }
            } catch (e) {
                onClose('error');
            }
        } else {
            generateReportFile();
        }
    };

    useEffect(() => {
        if (study && open) getObservations({ variables: { studyId: study.id, includeDeleted: false } });
        if (stateForGenerate && open) {
            getReportData({
                variables: {
                    studyId: study?.id,
                    dataSetId: stateForGenerate.dataset.id,
                    onlyReviewed: stateForGenerate.includeOnlyReviewedSites,
                },
            });
            loadStudy({
                variables: {
                    id: study?.id,
                    timestamp: stateForGenerate.dataset.created,
                },
            });
        }
    }, [stateForGenerate, open]);

    useEffect(() => {
        if (processedData?.sites) setStudySummary(calculateFieldsForStudySummary(processedData.sites));
        if (reportData && actualStudy?.study) {
            const config = scoringData?.config ?? processedData?.config;
            const riskOverviewColumn = calculateRiskOverviewColumns(config?.panels);
            const dataForPreview = calculateRiskOverviewsAndStudySites(
                processedData?.sites,
                scoringData?.siteScores,
                stateForGenerate.includeOnlyReviewedSites,
                actualStudy?.study?.sitesStatusesToExclude,
                config?.panels,
                riskOverviewColumn,
                reportData?.centralMonitoringReviewData?.studySites,
                true,
            );
            setRiskOverviewColumns(riskOverviewColumn);
            setRiskOverviews(dataForPreview.riskOverviews);
            setSites(
                dataForPreview.studySites.filter(
                    (item: any) => actualStudy?.study.sitesStatusesToExclude.indexOf(item.siteStatus) < 0,
                ),
            );
        }
    }, [processedData, scoringData, reportData, actualStudy]);

    useEffect(() => {
        setState(reportData?.centralMonitoringReviewData ?? {});
    }, [reportData]);

    useEffect(() => {
        const arrayOfsummaries = Object.values(siteReviews).map((item: any) => item.summary);
        changesWereMade.setValue(arrayOfsummaries.join('').length > 0);
    }, [siteReviews]);

    const close = (action?: string) => {
        if (changesWereMade.value) showWarningModal.setTrue();
        else {
            onClose(action);
            setSiteReviews({});
        }
    };

    const setSiteReviewInfo = (id: string, value: any) => {
        setSiteReviews({
            ...siteReviews,
            [id]: value,
        });
    };

    const onUpdateErrorState = (id: string, value: any) => {
        setErrors({
            ...errors,
            [id]: value,
        });
    };

    if (
        loading ||
        loadingReport ||
        loadingPDFReport ||
        loadingObservations ||
        loadingDataSetReview ||
        loadingStudy ||
        loadingDataset.value
    )
        return <Loading float />;

    return (
        <>
            <WarningLostDataDialog
                open={showWarningModal.value}
                onClose={(action: string) => {
                    if (action === 'close') {
                        changesWereMade.setFalse();
                        onClose();
                    }
                    showWarningModal.setFalse();
                }}
                customMessage={t('unsavedSummary')}
            />
            <CustomDialog
                className="risk-details-dialog"
                onClose={close}
                fullWidth
                fullScreen
                scroll="paper"
                open={open && !loadingReport && !showWarningModal.value}
            >
                <DialogTitle id="preview-report-modal-title">
                    {t('cm:CMReport')}
                    <FontAwesomeIcon
                        icon={['fal', 'times']}
                        className="fa-lg close-icon-cv"
                        onClick={() => {
                            close();
                        }}
                        data-testid="close-dialog"
                    />
                </DialogTitle>
                <DialogContent style={{ paddingLeft: 1, paddingRight: 1, paddingTop: 0 }}>
                    <div className="amount-sites-btn-wrapper">
                        <p className="amount-site">{t('cm:sitesInclude', { amount: sites.length })}</p>
                        <Button
                            className="summary-save-btn"
                            text="Generate"
                            disabled={Object.keys(errors).filter((item) => errors[item]).length > 0}
                            onClick={submit}
                        />
                    </div>
                    <div className="cm-report-content">
                        <SummaryInfo study={actualStudy?.study} studySummary={studySummary} />
                        <StudyReview
                            state={state}
                            setSiteReviewInfo={setSiteReviewInfo}
                            stateForGenerate={stateForGenerate}
                            reportData={reportData}
                            siteReviewInfo={siteReviews[Constants.ObjectId.Empty]}
                            onUpdateErrorState={onUpdateErrorState}
                        />
                        <div style={{ marginBottom: 30 }} />
                        <RiskOverviewSection
                            processedData={processedData}
                            study={actualStudy?.study}
                            riskOverviews={riskOverviews}
                            riskOverviewColumns={riskOverviewColumns}
                        />
                        <div style={{ marginBottom: 30 }} />
                        <ObservationsSection
                            data={observations?.centralMonitoringObservations ?? []}
                            dataSetId={stateForGenerate?.dataset.id}
                            dataSetDate={stateForGenerate?.dataset.created}
                            reports={reports}
                        />
                        <div style={{ marginBottom: 30 }} />
                        {sites?.length
                            ? sites.map((item: any) => (
                                  <SiteReport
                                      siteReviewInfo={siteReviews[item.siteId]}
                                      site={item}
                                      study={actualStudy?.study}
                                      studyReview={state.studyReview}
                                      dataSetId={stateForGenerate.dataset.id}
                                      setSiteReviews={setSiteReviewInfo}
                                      onUpdateErrorState={onUpdateErrorState}
                                  />
                              ))
                            : null}
                    </div>
                </DialogContent>
            </CustomDialog>
        </>
    );
};

export default PreviewReport;
